import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { api, type Session } from '../services/api'
import PageHeader from '../nexus/components/PageHeader'
import { useAnalyticsPeriod } from '../lib/analytics-period'
import ModelUsageBreakdown, { formatUsd } from '../components/ModelUsageBreakdown'

function SessionRow({ session, userId }: { session: Session; userId: string }) {
  const [open, setOpen] = useState(false)
  const messages = useQuery({
    queryKey: ['user-profile-session', session.session_id, userId],
    queryFn: () => api.getSessionMessages(session.session_id, userId),
    enabled: open,
    staleTime: 60_000,
  })

  return (
    <div className="collapse collapse-arrow border border-base-300 bg-base-100">
      <input type="checkbox" checked={open} onChange={(event) => setOpen(event.target.checked)} aria-label={`Open ${session.title || 'untitled session'}`} />
      <div className="collapse-title grid gap-2 pr-12 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-center">
        <span className="font-semibold truncate">{session.title || 'Untitled session'}</span>
        <span className="text-sm text-base-content/60">Active {formatDistanceToNow(new Date(session.updated_at), { addSuffix: true })}</span>
        <span className="badge badge-soft">{session.message_count ?? 0} user messages</span>
      </div>
      <div className="collapse-content">
        {messages.isLoading ? <div className="py-8 text-center"><span className="loading loading-spinner" /></div>
          : messages.error ? <p className="py-4 text-error">{messages.error instanceof Error ? messages.error.message : 'Failed to load conversation.'}</p>
          : messages.data?.length ? <div className="space-y-4 border-t border-base-300 pt-4">
            {messages.data.map((message) => <div key={message.message_id} className={`chat ${message.role === 'user' ? 'chat-start' : 'chat-end'}`}>
              <div className="chat-header text-xs opacity-60">{message.role === 'user' ? 'User' : 'Assistant'}</div>
              <div className={`chat-bubble max-w-3xl ${message.role === 'user' ? 'chat-bubble-primary' : 'chat-bubble-secondary'}`}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
              </div>
            </div>)}
          </div> : <p className="py-4 text-base-content/60">No messages recorded for this session.</p>}
      </div>
    </div>
  )
}

export default function UserProfile() {
  const { userId = '' } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { period } = useAnalyticsPeriod()
  const profile = useQuery({ queryKey: ['user-profile', userId, period], queryFn: () => api.getUser(userId, period), enabled: Boolean(userId) })
  const sessions = useQuery({
    queryKey: ['user-profile-sessions', userId, period],
    queryFn: () => api.getUserSessions(userId, period),
    enabled: Boolean(userId) && profile.data?.privacy_restricted === false,
  })

  if (profile.isLoading) return <div className="p-8 text-center"><span className="loading loading-spinner loading-lg" /></div>
  if (profile.error || !profile.data) return <div className="p-6"><button className="btn btn-ghost mb-4" onClick={() => navigate('/users')}>← Back to users</button><div className="alert alert-error">{profile.error instanceof Error ? profile.error.message : 'User not found.'}</div></div>

  const user = profile.data
  const periodLabel = period === 'day' ? 'Day' : period === 'week' ? 'Week' : period === 'month' ? 'Month' : 'Lifetime'
  const statusTone = user.account_status === 'Subscribed' ? 'badge-success' : user.account_status === 'Helpbot' ? 'badge-info' : user.account_status === 'Staff' ? 'badge-secondary' : 'badge-warning'
  return (
    <div className="p-6">
      <button className="btn btn-ghost btn-sm mb-4" onClick={() => navigate('/users')}>← Back to users</button>
      <PageHeader title={user.name || 'Unknown user'} subtitle={user.email || user.user_id} actions={<span className={`badge badge-soft ${statusTone}`}>{user.account_status}</span>} />

      <div className="grid gap-4 lg:grid-cols-3">
        <section className="card bg-base-100 border border-base-300 lg:col-span-2"><div className="card-body">
          <h2 className="card-title">Profile</h2>
          <dl className="grid gap-4 sm:grid-cols-2">
            <div><dt className="text-xs uppercase text-base-content/50">Current status</dt><dd className="font-medium">{user.account_status}</dd></div>
            <div><dt className="text-xs uppercase text-base-content/50">Associated organization</dt><dd className="font-medium">{user.org_name || 'N/A'}</dd></div>
            <div><dt className="text-xs uppercase text-base-content/50">Free-trial terms</dt><dd className="font-medium">{user.terms_status}</dd></div>
            <div><dt className="text-xs uppercase text-base-content/50">Allow ASM follow-up</dt><dd className="font-medium">{user.followup_contact_enabled ? 'On' : 'Off'}{!user.followup_contact_configured && <span className="text-xs text-base-content/50"> (default)</span>}</dd></div>
            <div><dt className="text-xs uppercase text-base-content/50">Created</dt><dd className="font-medium">{new Date(user.created_at).toLocaleString()}</dd></div>
            <div><dt className="text-xs uppercase text-base-content/50">Last active</dt><dd className="font-medium">{user.last_active ? formatDistanceToNow(new Date(user.last_active), { addSuffix: true }) : 'Never'}</dd></div>
          </dl>
        </div></section>

        <section className="card bg-base-100 border border-base-300"><div className="card-body">
          <h2 className="card-title">{periodLabel} usage</h2>
          {user.privacy_restricted ? <div className="alert alert-info text-sm">Individual usage is hidden for paid subscribers. Use organization reports for anonymous totals.</div> : <dl className="stats stats-vertical shadow-none">
            <div className="stat px-0"><dt className="stat-title">User messages</dt><dd className="stat-value text-2xl">{(user.period_messages ?? 0).toLocaleString()}</dd></div>
            <div className="stat px-0"><dt className="stat-title">Sessions</dt><dd className="stat-value text-2xl">{(user.period_sessions ?? 0).toLocaleString()}</dd></div>
            <div className="stat px-0"><dt className="stat-title">Token burn</dt><dd className="stat-value text-2xl">{(user.period_tokens ?? 0).toLocaleString()}</dd></div>
            <div className="stat px-0"><dt className="stat-title">Spend</dt><dd className="stat-value text-2xl">{formatUsd(user.period_spend_usd)}</dd>{user.has_unpriced_models && <div className="stat-desc text-warning">Plus unpriced model usage</div>}</div>
            <div className="stat px-0"><dt className="stat-title">By model</dt><dd className="mt-1"><ModelUsageBreakdown usage={user.model_usage} /></dd></div>
          </dl>}
        </div></section>
      </div>

      <section className="mt-6">
        <h2 className="text-xl font-semibold mb-1">Sessions</h2>
        <p className="text-sm text-base-content/60 mb-3">Active during the selected {periodLabel.toLowerCase()} period.</p>
        {user.privacy_restricted ? <div className="card bg-base-100 border border-base-300"><div className="card-body text-base-content/70">Session details are not available for paid subscribers.</div></div>
          : sessions.isLoading ? <div className="py-8 text-center"><span className="loading loading-spinner" /></div>
          : sessions.error ? <div className="alert alert-error">{sessions.error instanceof Error ? sessions.error.message : 'Failed to load sessions.'}</div>
          : sessions.data?.length ? <div className="space-y-3">{sessions.data.map((session) => <SessionRow key={session.session_id} session={session} userId={userId} />)}</div>
          : <div className="card bg-base-100 border border-base-300"><div className="card-body text-base-content/60">No sessions recorded.</div></div>}
      </section>
    </div>
  )
}
